import React, { useEffect, useState } from 'react';
import { 
  Modal, Input, Form, Button, Card, Tag, 
  Steps, message, Spin, Alert, Divider, Typography, Space, Result
} from 'antd';
import { 
  ThunderboltOutlined, 
  WalletOutlined, 
  HomeOutlined,
  CreditCardOutlined,
  SafetyOutlined,
  MobileOutlined,
  WifiOutlined,
  FireOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BankOutlined,
  UserOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  PrinterOutlined,
  DownloadOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'antd/dist/antd.css';
import { isNumber } from '../../Utils/Common';
import { 
  BILLERS_API,
  FETCH_BILL_API,
  FETCH_CREDIT_CARD_BILL_API,
  PAY_BILL_API
} from '../../Utils/appConstants';
import { getNewRequest, postRequest } from '../../Utils/AppApi';
import { useHistory } from "react-router-dom";

const { Step } = Steps;
const { Title, Text, Paragraph } = Typography;

function CommonBillPayment({ category, categoryId }) {
  const history = useHistory();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [billers, setBillers] = useState([]);
  const [selectedBiller, setSelectedBiller] = useState(null);
  const [billDetails, setBillDetails] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [formData, setFormData] = useState({
    biller_op_code: '',
    category_name: category,
    number: '',
    mobile: '',
    card_last_4_digit_number: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const [isCreditCard, setIsCreditCard] = useState(false);

  // Category configuration
  const categoryConfig = {
    'ELECTRICITY': {
      name: 'Electricity Bill',
      icon: <ThunderboltOutlined />,
      color: '#f59e0b',
      lightColor: '#fef3c7',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      description: 'Pay your electricity bill instantly',
      placeholder: 'Enter Consumer Number'
    },
    'WATER BILL': {
      name: 'Water Bill',
      icon: <i className="fas fa-tint" />,
      color: '#3b82f6',
      lightColor: '#dbeafe',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      description: 'Pay your water bill easily',
      placeholder: 'Enter Water Connection ID'
    },
    'FASTAG': {
      name: 'Fastag Recharge',
      icon: <i className="fas fa-tag" />,
      color: '#10b981',
      lightColor: '#d1fae5',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      description: 'Recharge your Fastag instantly',
      placeholder: 'Enter Vehicle Number (e.g., RJ45CR2459)'
    },
    'POSTPAID': {
      name: 'Mobile Postpaid',
      icon: <MobileOutlined />,
      color: '#8b5cf6',
      lightColor: '#ede9fe',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      description: 'Pay your postpaid mobile bill',
      placeholder: 'Enter Mobile Number'
    },
    'BROADBAND': {
      name: 'Broadband Bill',
      icon: <WifiOutlined />,
      color: '#06b6d4',
      lightColor: '#cffafe',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      description: 'Pay your broadband bill',
      placeholder: 'Enter Account Number'
    },
    'CREDIT_CARD': {
      name: 'Credit Card Bill',
      icon: <CreditCardOutlined />,
      color: '#ef4444',
      lightColor: '#fee2e2',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      description: 'Pay your credit card bill',
      placeholder: 'Last 4 Digits'
    },
    'GAS': {
      name: 'Gas Bill',
      icon: <FireOutlined />,
      color: '#f97316',
      lightColor: '#ffedd5',
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      description: 'Pay your gas bill',
      placeholder: 'Enter Consumer Number'
    },
    'LPG': {
      name: 'LPG Gas Bill',
      icon: <FireOutlined />,
      color: '#f97316',
      lightColor: '#ffedd5',
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      description: 'Pay your LPG gas bill',
      placeholder: 'Enter Consumer Number'
    },
    'INSURANCE': {
      name: 'Insurance Premium',
      icon: <SafetyOutlined />,
      color: '#06b6d4',
      lightColor: '#cffafe',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      description: 'Pay your insurance premium',
      placeholder: 'Enter Policy Number'
    }
  };

  const currentConfig = categoryConfig[category] || {
    name: category?.replace(/_/g, ' ') || 'Bill Payment',
    icon: <HomeOutlined />,
    color: '#6b7280',
    lightColor: '#f3f4f6',
    gradient: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    description: 'Pay your bill securely',
    placeholder: 'Enter Account Number'
  };

  useEffect(() => {
    setIsCreditCard(category === 'CREDIT_CARD');
    fetchBillers();
  }, [category]);

  const fetchBillers = async () => {
    setLoading(true);
    try {
      const response = await getNewRequest(BILLERS_API, { category_name: category }, history);
      
      if (response && response.data && response.data.status === true) {
        const billersList = response.data.data.map(biller => ({
          id: biller.id,
          name: biller.name,
          op_code: biller.op_code
        }));
        setBillers(billersList);
        if (billersList.length === 0) {
          message.warning('No billers found for this category');
        }
      } else {
        message.error(response?.data?.message || 'Failed to fetch billers');
      }
    } catch (error) {
      console.error('Error fetching billers:', error);
      message.error('Failed to load billers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBillerChange = (e) => {
    const value = e.target.value;
    const selected = billers.find(b => b.op_code === value);
    setSelectedBiller(selected);
    setFormData({
      ...formData,
      biller_op_code: value
    });
    setFormErrors({ ...formErrors, biller_op_code: false });
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
    setFormErrors({ ...formErrors, [field]: false });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.biller_op_code) {
      errors.biller_op_code = true;
    }
    
    if (isCreditCard) {
      if (!formData.mobile || formData.mobile.length !== 10) {
        errors.mobile = true;
      }
      if (!formData.card_last_4_digit_number || formData.card_last_4_digit_number.length !== 4) {
        errors.card_last_4_digit_number = true;
      }
    } else {
      if (!formData.number) {
        errors.number = true;
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchBill = async () => {
    if (!validateForm()) {
      message.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (isCreditCard) {
        const payload = new FormData();
        payload.append('biller_op_code', formData.biller_op_code);
        payload.append('mobile', formData.mobile);
        payload.append('card_last_4_digit_number', formData.card_last_4_digit_number);
        response = await postRequest(FETCH_CREDIT_CARD_BILL_API, payload, history, 'multipart/form-data');
      } else {
        const payload = new FormData();
        payload.append('biller_op_code', formData.biller_op_code);
        payload.append('category_name', formData.category_name);
        payload.append('number', formData.number);
        response = await postRequest(FETCH_BILL_API, payload, history, 'multipart/form-data');
      }

      if (response && response.data && response.data.status === true) {
        setBillDetails(response.data.data);
        setCurrentStep(1);
        message.success('Bill fetched successfully');
      } else {
        message.error(response?.data?.message || 'Failed to fetch bill');
      }
    } catch (error) {
      console.error('Error fetching bill:', error);
      message.error('Failed to fetch bill. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async () => {
    setPaymentProcessing(true);
    try {
      const payload = new FormData();
      payload.append('biller_op_code', formData.biller_op_code);
      payload.append('amount', billDetails.bill_amount || billDetails.amount);
      
      if (isCreditCard) {
        payload.append('params[mobile]', formData.mobile);
        payload.append('params[card_last_4_digit_number]', formData.card_last_4_digit_number);
      } else {
        payload.append('params[number]', formData.number);
      }

      const response = await postRequest(PAY_BILL_API, payload, history, 'multipart/form-data');

      if (response && response.data && response.data.status === true) {
        setPaymentResult(response.data.data);
        setCurrentStep(2);
        message.success(response.data.message || 'Payment successful!');
      } else {
        message.error(response?.data?.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      message.error('Payment failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      biller_op_code: '',
      category_name: category,
      number: '',
      mobile: '',
      card_last_4_digit_number: ''
    });
    setSelectedBiller(null);
    setBillDetails(null);
    setPaymentResult(null);
    setCurrentStep(0);
    setFormErrors({});
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderBillFetchForm();
      case 1:
        return renderBillDetails();
      case 2:
        return renderPaymentConfirmation();
      default:
        return renderBillFetchForm();
    }
  };

  const renderBillFetchForm = () => {
    return (
      <div className="elegant-fetch-form">
        {/* Native Biller Select */}
        <div className="form-group mb-4">
          <label className="elegant-form-label">Select Biller <span className="text-danger">*</span></label>
          <select 
            className={`form-control elegant-native-select ${formErrors.biller_op_code ? 'is-invalid' : ''}`}
            value={formData.biller_op_code}
            onChange={handleBillerChange}
            disabled={loading}
          >
            <option value="">Select Your Service Provider</option>
            {billers.map(biller => (
              <option key={biller.op_code} value={biller.op_code}>
                {biller.name}
              </option>
            ))}
          </select>
          {formErrors.biller_op_code && <span className="text-danger small">Please select a biller</span>}
        </div>

        {/* Account Number Field */}
        {!isCreditCard && (
          <div className="form-group mb-4">
            <label className="elegant-form-label">Account / Consumer Number <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-control elegant-native-input ${formErrors.number ? 'is-invalid' : ''}`}
              placeholder={currentConfig.placeholder}
              value={formData.number}
              onChange={(e) => handleInputChange('number', e.target.value)}
              maxLength={50}
            />
            {formErrors.number && <span className="text-danger small">Please enter account number</span>}
          </div>
        )}

        {/* Credit Card Fields */}
        {isCreditCard && (
          <>
            <div className="form-group mb-4">
              <label className="elegant-form-label">Mobile Number <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-control elegant-native-input ${formErrors.mobile ? 'is-invalid' : ''}`}
                placeholder="Enter registered mobile number"
                value={formData.mobile}
                onChange={(e) => handleInputChange('mobile', e.target.value)}
                maxLength={10}
                onKeyPress={isNumber}
              />
              {formErrors.mobile && <span className="text-danger small">Please enter valid 10-digit mobile number</span>}
            </div>

            <div className="form-group mb-4">
              <label className="elegant-form-label">Last 4 Digits of Card <span className="text-danger">*</span></label>
              <input
                type="password"
                className={`form-control elegant-native-input ${formErrors.card_last_4_digit_number ? 'is-invalid' : ''}`}
                placeholder="Enter last 4 digits"
                value={formData.card_last_4_digit_number}
                onChange={(e) => handleInputChange('card_last_4_digit_number', e.target.value)}
                maxLength={4}
                onKeyPress={isNumber}
              />
              {formErrors.card_last_4_digit_number && <span className="text-danger small">Please enter last 4 digits</span>}
            </div>
          </>
        )}

        <button
          className="elegant-fetch-button"
          onClick={fetchBill}
          disabled={loading}
          style={{ background: currentConfig.gradient }}
        >
          {loading ? <Spin size="small" /> : <><FileTextOutlined /> Fetch Bill Details</>}
        </button>
      </div>
    );
  };

  const renderBillDetails = () => {
    if (!billDetails) return null;

    const billAmount = billDetails.bill_amount || billDetails.amount;
    const dueDate = billDetails.due_date || billDetails.dueDate;
    const billDate = billDetails.bill_date || billDetails.billDate;
    const customerName = billDetails.customer_name || billDetails.customerName;

    return (
      <div className="elegant-bill-details">
        <div className="elegant-amount-card" style={{ borderColor: currentConfig.color, background: currentConfig.lightColor }}>
          <div className="elegant-amount-label">Total Amount Due</div>
          <div className="elegant-amount" style={{ color: currentConfig.color }}>₹{billAmount}</div>
          {dueDate && (
            <div className="elegant-due-date">
              <ClockCircleOutlined /> Due by {dueDate}
            </div>
          )}
        </div>

        <div className="elegant-details-grid">
          {customerName && (
            <div className="elegant-detail-card">
              <UserOutlined className="elegant-detail-icon" style={{ color: currentConfig.color }} />
              <div className="elegant-detail-content">
                <div className="elegant-detail-label">Customer Name</div>
                <div className="elegant-detail-value">{customerName}</div>
              </div>
            </div>
          )}
          {billDate && (
            <div className="elegant-detail-card">
              <FileTextOutlined className="elegant-detail-icon" style={{ color: currentConfig.color }} />
              <div className="elegant-detail-content">
                <div className="elegant-detail-label">Bill Date</div>
                <div className="elegant-detail-value">{billDate}</div>
              </div>
            </div>
          )}
          {billDetails.bill_number && (
            <div className="elegant-detail-card">
              <CreditCardOutlined className="elegant-detail-icon" style={{ color: currentConfig.color }} />
              <div className="elegant-detail-content">
                <div className="elegant-detail-label">Bill Number</div>
                <div className="elegant-detail-value">{billDetails.bill_number}</div>
              </div>
            </div>
          )}
        </div>

        <div className="elegant-actions">
          <button className="elegant-back-button" onClick={() => setCurrentStep(0)}>
            <ArrowLeftOutlined /> Back
          </button>
          <button
            className="elegant-pay-button"
            onClick={processPayment}
            disabled={paymentProcessing}
            style={{ background: currentConfig.gradient }}
          >
            {paymentProcessing ? <Spin size="small" /> : <><WalletOutlined /> Pay ₹{billAmount}</>}
          </button>
        </div>
      </div>
    );
  };

  const renderPaymentConfirmation = () => {
    if (!paymentResult) return null;

    return (
      <div className="elegant-success">
        <div className="elegant-success-icon" style={{ background: currentConfig.gradient }}>
          <CheckCircleOutlined />
        </div>
        <h3 className="elegant-success-title">Payment Successful!</h3>
        <p className="elegant-success-subtitle">Your bill has been paid successfully</p>

        <div className="elegant-transaction-card">
          <div className="elegant-transaction-header">
            <SafetyCertificateOutlined /> Transaction Details
          </div>
          <div className="elegant-transaction-body">
            <div className="elegant-transaction-row">
              <span className="elegant-transaction-label">Transaction ID:</span>
              <span className="elegant-transaction-value">{paymentResult.txn_id}</span>
            </div>
            <div className="elegant-transaction-row">
              <span className="elegant-transaction-label">Status:</span>
              <Tag color="success" className="elegant-status-tag">{paymentResult.status}</Tag>
            </div>
            {paymentResult.rrn && (
              <div className="elegant-transaction-row">
                <span className="elegant-transaction-label">RRN Number:</span>
                <span className="elegant-transaction-value">{paymentResult.rrn}</span>
              </div>
            )}
            <div className="elegant-transaction-row">
              <span className="elegant-transaction-label">Amount Paid:</span>
              <span className="elegant-transaction-value elegant-paid-amount">₹{billDetails?.bill_amount || billDetails?.amount}</span>
            </div>
            <div className="elegant-transaction-row">
              <span className="elegant-transaction-label">Payment Date:</span>
              <span className="elegant-transaction-value">{moment().format('DD MMM YYYY, hh:mm A')}</span>
            </div>
          </div>
        </div>

        <button
          className="elegant-new-payment"
          onClick={resetForm}
          style={{ background: currentConfig.gradient }}
        >
          <ReloadOutlined /> Make Another Payment
        </button>
      </div>
    );
  };

  return (
    <div className="elegant-payment-wrapper">
      <div className="elegant-payment-card">
        {/* Elegant Header */}
        <div className="elegant-header" style={{ borderBottomColor: currentConfig.color }}>
          <div className="elegant-header-content">
            <div className="elegant-icon-wrapper" style={{ background: currentConfig.lightColor, color: currentConfig.color }}>
              {currentConfig.icon}
            </div>
            <div className="elegant-header-text">
              <h2>{currentConfig.name}</h2>
              <p>{currentConfig.description}</p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="elegant-steps">
          <Steps current={currentStep} size="small">
            <Step title="Enter Details" />
            <Step title="Verify Bill" />
            <Step title="Payment" />
          </Steps>
        </div>

        {/* Content */}
        <div className="elegant-content">
          {renderStepContent()}
        </div>
      </div>

      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default CommonBillPayment;